import React, { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Download, Users, BookOpen, Target, TrendingUp } from 'lucide-react';
import { TestResults } from '../../types';

// Lazy load detailed guides
const DetailedGuide = lazy(() => import('./DetailedGuide'));

interface NextStepsProps {
  results: TestResults;
}

const NextSteps: React.FC<NextStepsProps> = ({ results }) => {
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  // Get the three weakest categories for targeted recommendations
  const weakestCategories = [...results.categories]
    .sort((a, b) => a.percentage - b.percentage)
    .slice(0, 3);

  const actionCards = [
    {
      id: 'focus-weak-areas',
      icon: Target,
      title: 'Focus on Weak Areas',
      description: 'Address the categories where you scored lowest to improve your overall export readiness.',
      priority: 'High',
      timeframe: '1-3 months',
      resources: [
        { name: 'Export Readiness Improvement Guide', type: 'PDF', action: () => setSelectedGuide('improvement-guide') },
        { name: 'Category-Specific Action Plans', type: 'Templates', action: () => setSelectedGuide('action-plans') },
        { name: 'Self-Assessment Checklist', type: 'Tool', action: () => setSelectedGuide('checklist') }
      ],
      weakestCategory: weakestCategories[0]
    },
    {
      id: 'expert-guidance',
      icon: Users,
      title: 'Seek Expert Guidance',
      description: 'Connect with export development agencies and consultants for specialized assistance.',
      priority: 'Medium',
      timeframe: '2-4 weeks',
      resources: [
        { name: 'Find Export Consultants', type: 'Directory', url: 'https://www.trade.gov/find-export-assistance' },
        { name: 'SBA Export Programs', type: 'External', url: 'https://www.sba.gov/business-guide/grow-your-business/export-products' },
        { name: 'SCORE Mentorship', type: 'External', url: 'https://www.score.org/' }
      ]
    },
    {
      id: 'action-plan',
      icon: BookOpen,
      title: 'Develop an Action Plan',
      description: 'Create a strategic roadmap to address gaps and prepare for successful export operations.',
      priority: 'High',
      timeframe: '1-2 months',
      resources: [
        { name: 'Export Business Plan Template', type: 'Template', action: () => setSelectedGuide('business-plan') },
        { name: 'Market Entry Strategy Guide', type: 'Guide', action: () => setSelectedGuide('market-entry') },
        { name: 'Export Timeline Planner', type: 'Tool', action: () => setSelectedGuide('timeline-planner') }
      ]
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'High': return 'bg-error-100 text-error-700 border-error-200';
      case 'Medium': return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'Low': return 'bg-success-100 text-success-700 border-success-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    >
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-8 h-8 text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-800">Your Next Steps to Export Success</h2>
      </div>
      
      <p className="text-gray-700 mb-8 text-lg">
        Based on your results, here are personalized recommendations to improve your export readiness:
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {actionCards.map((card, index) => {
          const IconComponent = card.icon;
          
          return (
            <motion.div
              key={card.id}
              className="bg-white rounded-xl shadow-card p-6 hover:shadow-lg transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 + 0.6, duration: 0.4 }}
              whileHover={{ y: -4 }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <IconComponent className="w-6 h-6 text-primary-600" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-800">{card.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(card.priority)}`}>
                      {card.priority}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3">{card.description}</p>
                  
                  <div className="text-xs text-gray-500 mb-4">
                    <span className="font-medium">Timeline:</span> {card.timeframe}
                  </div>
                </div>
              </div>
              
              {/* Show weakest category for first card */}
              {card.weakestCategory && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="text-xs font-medium text-gray-700 mb-1">Priority Focus Area:</div>
                  <div className="text-sm font-semibold text-primary-600">
                    {card.weakestCategory.categoryId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                    <span className="text-gray-500 ml-2">({Math.round(card.weakestCategory.percentage)}%)</span>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 text-sm">Resources & Tools:</h4>
                
                {card.resources.map((resource, resourceIndex) => (
                  <motion.button
                    key={resourceIndex}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    onClick={resource.action || (() => resource.url && window.open(resource.url, '_blank'))}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{resource.name}</div>
                      <div className="text-xs text-gray-500">{resource.type}</div>
                    </div>
                    
                    {resource.url ? (
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Download className="w-4 h-4 text-gray-400" />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Progress Tracking Section */}
      <motion.div
        className="mt-8 bg-white rounded-xl p-6 shadow-card"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <h3 className="text-lg font-bold text-gray-800 mb-4">Track Your Progress</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Recommended Timeline</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Weeks 1-2: Focus on immediate improvements</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-primary-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Weeks 3-6: Implement systematic changes</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-primary-300 rounded-full"></div>
                <span className="text-sm text-gray-600">Weeks 7-12: Monitor and refine processes</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Success Metrics</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Increase overall readiness score by 15-20%</li>
              <li>• Achieve "Ready" status in 2-3 weak categories</li>
              <li>• Complete export business plan</li>
              <li>• Establish key partnerships and processes</li>
            </ul>
          </div>
        </div>
      </motion.div>
      
      {/* Lazy loaded detailed guides */}
      <Suspense fallback={<div>Loading guide...</div>}>
        {selectedGuide && (
          <DetailedGuide 
            guideId={selectedGuide} 
            onClose={() => setSelectedGuide(null)} 
          />
        )}
      </Suspense>
    </motion.div>
  );
};

export default NextSteps;